import { createHash } from 'node:crypto';
import { promises as fs } from 'node:fs';
import path from 'node:path';
import ignore, { type Ignore } from 'ignore';
import YAML from 'yaml';
import { IdeDetectionService } from './ide-detection.service.js';
import { ProjectConfigService } from './project-config.service.js';
import type { Jai1ProjectConfig } from './project-config.service.js';
import type {
    ProjectInspectionDegradedReason,
    ProjectInspectionResult,
    ProjectInspectionSignals,
    ProjectSignalGroup,
} from '../types/project-inspector.types.js';

const GENERATED_DIRECTORIES = new Set([
    '.git',
    '.secrets',
    '.secret',
    '.vault',
    '.next',
    '.nuxt',
    '.svelte-kit',
    '.turbo',
    '.venv',
    '.cache',
    '.pytest_cache',
    '.mypy_cache',
    '.ruff_cache',
    '.agents',
    '.codex',
    '.claude',
    '.cursor',
    '.windsurf',
    '.antigravity',
    '.opencode',
    '.jai1',
    'node_modules',
    'dist',
    'build',
    'output',
    '.output',
    'coverage',
    'target',
    'vendor',
    '__pycache__',
]);

const AUXILIARY_ROOT_DIRECTORIES = new Set([
    'archive',
    'archives',
    'doc',
    'docs',
    'example',
    'examples',
    'sample',
    'samples',
    'starter-kit',
    'starter-kits',
    'template',
    'templates',
]);

const SIGNAL_GROUPS: ProjectSignalGroup[] = [
    'languages',
    'frameworks',
    'workspaces',
    'commands',
    'testing',
    'database',
    'deployment',
    'activeIdes',
];

const FRAMEWORK_DEPENDENCIES: Record<string, string> = {
    nuxt: 'nuxt',
    next: 'nextjs',
    react: 'react',
    vue: 'vue',
    svelte: 'svelte',
    astro: 'astro',
    '@angular/core': 'angular',
    hono: 'hono',
    express: 'express',
    '@nestjs/core': 'nestjs',
    '@tauri-apps/api': 'tauri',
};

const TEST_DEPENDENCIES: Record<string, string> = {
    vitest: 'vitest',
    jest: 'jest',
    '@playwright/test': 'playwright',
    cypress: 'cypress',
    mocha: 'mocha',
};

const DATABASE_DEPENDENCIES: Record<string, string> = {
    'drizzle-orm': 'drizzle',
    prisma: 'prisma',
    '@prisma/client': 'prisma',
    sequelize: 'sequelize',
    typeorm: 'typeorm',
    mongoose: 'mongoose',
    pg: 'postgresql',
    mysql2: 'mysql',
    redis: 'redis',
};

interface PackageManifest {
    scripts?: Record<string, unknown>;
    dependencies?: Record<string, unknown>;
    devDependencies?: Record<string, unknown>;
    peerDependencies?: Record<string, unknown>;
    workspaces?: string[] | { packages?: string[] };
}

interface ManifestDependencyEvidence {
    section: 'dependencies' | 'devDependencies' | 'peerDependencies';
    name: string;
    version: string;
}

interface ManifestEvidence {
    path: string;
    dependencies: ManifestDependencyEvidence[];
    scripts: Array<{ name: string; body: string }>;
}

interface InspectionEvidence {
    manifests: ManifestEvidence[];
    configs: string[];
}

export type ProjectInspectorScanPhase = 'workspace-discovery' | 'project-scan';

export interface ProjectInspectorOptions {
    onDirectoryRead?: (phase: ProjectInspectorScanPhase, relativePath: string) => void;
    loadActiveIdeIds?: () => Promise<string[]>;
    loadProjectConfig?: () => Promise<Jai1ProjectConfig | null>;
}

type RecoverableRead<T> =
    | { ok: true; value: T }
    | { ok: false; value: T; reason: ProjectInspectionDegradedReason };

function compareCodeUnits(left: string, right: string): number {
    return left < right ? -1 : left > right ? 1 : 0;
}

function sorted(values: Iterable<string>): string[] {
    return [...new Set(values)].sort(compareCodeUnits);
}

function canonicalInspection(signals: ProjectInspectionSignals, evidence: InspectionEvidence): string {
    return JSON.stringify({
        signals: Object.fromEntries(SIGNAL_GROUPS.map((group) => [group, signals[group]])),
        evidence,
    });
}

function workspacePatternRegex(pattern: string): RegExp {
    const normalized = pattern.replace(/^\.\//, '').replace(/\/$/, '');
    let expression = '^';
    for (let index = 0; index < normalized.length; index += 1) {
        const character = normalized.charAt(index);
        const next = normalized.charAt(index + 1);
        if (character === '*' && next === '*') {
            expression += '.*';
            index += 1;
        } else if (character === '*') expression += '[^/]*';
        else if (character === '?') expression += '[^/]';
        else expression += character.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    return new RegExp(`${expression}$`);
}

export class ProjectInspectorService {
    constructor(
        private readonly projectRoot: string = process.cwd(),
        private readonly options: ProjectInspectorOptions = {}
    ) {}

    async inspect(): Promise<ProjectInspectionResult> {
        const gitignore = await this.loadGitignore();
        const workspacePatterns = await this.loadWorkspacePatterns();
        const projectRoots = await this.discoverProjectRoots(workspacePatterns, gitignore);
        const [files, ideRead, configRead] = await Promise.all([
            this.listProjectFiles(projectRoots, gitignore),
            this.readRecoverable(
                this.options.loadActiveIdeIds
                    ?? (() => new IdeDetectionService(this.projectRoot).getActiveIdeIds()),
                [],
                'ide-detection-unavailable',
            ),
            this.readRecoverable(
                this.options.loadProjectConfig
                    ?? (() => new ProjectConfigService(this.projectRoot).load()),
                null,
                'project-config-unreadable',
            ),
        ]);
        const activeIdes = ideRead.value;
        const config = configRead.value;
        const degradedReasons: ProjectInspectionDegradedReason[] = [];
        if (!configRead.ok) degradedReasons.push(configRead.reason);
        if (!ideRead.ok) degradedReasons.push(ideRead.reason);
        const { signals, evidence } = await this.detectSignals(files, activeIdes, projectRoots);
        const current = createHash('sha256').update(canonicalInspection(signals, evidence)).digest('hex');
        const state = config?.coreV3;
        const initialized = state?.schemaVersion === 1 && state.primaryPackage === 'core-v3';
        const baseline = initialized ? state.baselineFingerprint : null;
        const changed = baseline !== null && baseline !== current;
        const meaningful = SIGNAL_GROUPS
            .filter((group) => group !== 'activeIdes')
            .some((group) => signals[group].length > 0);

        let status: ProjectInspectionResult['status'];
        if (degradedReasons.length > 0) status = 'degraded';
        else if (!initialized) status = 'uninitialized';
        else if (!meaningful) status = 'needs_input';
        else if (changed) status = 'drifted';
        else status = 'ready';

        return {
            schemaVersion: 1,
            status,
            primaryPackage: initialized ? state.primaryPackage : null,
            fingerprint: { current, baseline, changed },
            signals,
            changedSignals: this.changedSignals(signals, state?.baselineSignals, changed),
            missingInputs: meaningful ? [] : ['project-signals'],
            degradedReasons,
        };
    }

    private async readRecoverable<T>(
        read: () => Promise<T>,
        fallback: T,
        reason: ProjectInspectionDegradedReason,
    ): Promise<RecoverableRead<T>> {
        try {
            return { ok: true, value: await read() };
        } catch {
            return { ok: false, value: fallback, reason };
        }
    }

    private async loadGitignore(): Promise<Ignore> {
        const matcher = ignore();
        try {
            matcher.add(await fs.readFile(path.join(this.projectRoot, '.gitignore'), 'utf-8'));
        } catch {
            // A root .gitignore is optional.
        }
        return matcher;
    }

    private async loadWorkspacePatterns(): Promise<string[]> {
        const patterns = new Set<string>();
        const rootManifest = await this.readPackageManifest('package.json');
        const manifestWorkspaces = Array.isArray(rootManifest?.workspaces)
            ? rootManifest.workspaces
            : rootManifest?.workspaces?.packages;
        for (const pattern of manifestWorkspaces ?? []) patterns.add(pattern);

        try {
            const content = await fs.readFile(path.join(this.projectRoot, 'pnpm-workspace.yaml'), 'utf-8');
            const parsed = YAML.parse(content) as { packages?: unknown } | null;
            if (Array.isArray(parsed?.packages)) {
                for (const pattern of parsed.packages) {
                    if (typeof pattern === 'string') patterns.add(pattern);
                }
            }
        } catch {
            // pnpm workspace configuration is optional.
        }
        return sorted(patterns);
    }

    private async discoverProjectRoots(patterns: string[], gitignore: Ignore): Promise<string[]> {
        const roots = new Set<string>(['']);
        if (patterns.length === 0) return [''];
        const includedWorkspacePatterns = patterns.filter((pattern) => !pattern.startsWith('!'));
        const includePatterns = includedWorkspacePatterns.map(workspacePatternRegex);
        const excludePatterns = patterns
            .filter((pattern) => pattern.startsWith('!'))
            .map((pattern) => workspacePatternRegex(pattern.slice(1)));

        const visit = async (directory: string, relativeDirectory: string): Promise<void> => {
            this.options.onDirectoryRead?.('workspace-discovery', relativeDirectory || '.');
            let entries: Awaited<ReturnType<typeof fs.readdir>>;
            try {
                entries = await fs.readdir(directory, { withFileTypes: true });
            } catch {
                return;
            }
            entries.sort((left, right) => compareCodeUnits(left.name, right.name));
            if (
                relativeDirectory
                && entries.some((entry) => entry.isFile() && entry.name === 'package.json')
            ) {
                const included = includePatterns.some((pattern) => pattern.test(relativeDirectory));
                const excluded = excludePatterns.some((pattern) => pattern.test(relativeDirectory));
                if (included && !excluded) roots.add(relativeDirectory);
            }
            for (const entry of entries) {
                const relativePath = relativeDirectory
                    ? path.posix.join(relativeDirectory, entry.name)
                    : entry.name;
                if (entry.isDirectory()) {
                    if (this.isIgnoredDirectory(relativePath, entry.name, gitignore)) continue;
                    if (!this.canMatchWorkspaceAtOrBelow(relativePath, includedWorkspacePatterns)) continue;
                    await visit(path.join(directory, entry.name), relativePath);
                }
            }
        };
        await visit(this.projectRoot, '');
        return sorted(roots);
    }

    private canMatchWorkspaceAtOrBelow(relativeDirectory: string, patterns: string[]): boolean {
        const directorySegments = relativeDirectory.split('/');
        return patterns.some((pattern) => {
            const patternSegments = pattern.replace(/^\.\//, '').replace(/\/$/, '').split('/');
            for (let index = 0; index < directorySegments.length; index += 1) {
                const patternSegment = patternSegments[index];
                const directorySegment = directorySegments[index];
                if (patternSegment === '**') return true;
                if (!patternSegment || !directorySegment || !workspacePatternRegex(patternSegment).test(directorySegment)) {
                    return false;
                }
            }
            return directorySegments.length <= patternSegments.length;
        });
    }

    private async listProjectFiles(projectRoots: string[], gitignore: Ignore): Promise<string[]> {
        const files = new Set<string>();
        const projectRootSet = new Set(projectRoots);
        for (const projectRoot of projectRoots) {
            const absoluteRoot = path.join(this.projectRoot, projectRoot);
            const visit = async (directory: string, relativeWithinRoot: string): Promise<void> => {
                const visitedPath = projectRoot
                    ? path.posix.join(projectRoot, relativeWithinRoot)
                    : relativeWithinRoot;
                const normalizedVisitedPath = visitedPath.replace(/^\//, '') || '.';
                this.options.onDirectoryRead?.('project-scan', normalizedVisitedPath);
                let entries: Awaited<ReturnType<typeof fs.readdir>>;
                try {
                    entries = await fs.readdir(directory, { withFileTypes: true });
                } catch {
                    return;
                }
                entries.sort((left, right) => compareCodeUnits(left.name, right.name));
                const currentProjectPath = normalizedVisitedPath === '.' ? '' : normalizedVisitedPath;
                if (
                    relativeWithinRoot
                    && !projectRootSet.has(currentProjectPath)
                    && entries.some((entry) => entry.isFile() && entry.name === 'package.json')
                ) {
                    return;
                }
                for (const entry of entries) {
                    const relativePath = projectRoot
                        ? path.posix.join(projectRoot, relativeWithinRoot, entry.name)
                        : path.posix.join(relativeWithinRoot, entry.name);
                    const normalizedPath = relativePath.replace(/^\//, '');
                    if (entry.isDirectory()) {
                        if (this.isIgnoredDirectory(normalizedPath, entry.name, gitignore)) continue;
                        if (projectRootSet.has(normalizedPath) && normalizedPath !== projectRoot) continue;
                        if (!relativeWithinRoot && AUXILIARY_ROOT_DIRECTORIES.has(entry.name)) continue;
                        await visit(path.join(directory, entry.name), path.posix.join(relativeWithinRoot, entry.name));
                    } else if (
                        entry.isFile()
                        && !entry.name.startsWith('.env')
                        && !gitignore.ignores(normalizedPath)
                    ) {
                        files.add(normalizedPath);
                    }
                }
            };
            await visit(absoluteRoot, '');
        }
        return sorted(files);
    }

    private isIgnoredDirectory(relativePath: string, name: string, gitignore: Ignore): boolean {
        if (GENERATED_DIRECTORIES.has(name)) return true;
        if (relativePath === '.jai1/local' || relativePath.startsWith('.jai1/local/')) return true;
        return gitignore.ignores(`${relativePath}/`);
    }

    private async detectSignals(
        files: string[],
        activeIdeIds: string[],
        projectRoots: string[]
    ): Promise<{ signals: ProjectInspectionSignals; evidence: InspectionEvidence }> {
        const languages = new Set<string>();
        const frameworks = new Set<string>();
        const workspaces = new Set<string>();
        const commands = new Set<string>();
        const testing = new Set<string>();
        const database = new Set<string>();
        const deployment = new Set<string>();
        const fileSet = new Set(files);

        if (files.some((file) => file.endsWith('.ts') || file.endsWith('.tsx')) || this.hasBasename(files, 'tsconfig.json')) {
            languages.add('typescript');
        }
        if (files.some((file) => file.endsWith('.js') || file.endsWith('.jsx'))) languages.add('javascript');
        if (files.some((file) => file.endsWith('.py'))) languages.add('python');
        if (files.some((file) => file.endsWith('.go'))) languages.add('go');
        if (files.some((file) => file.endsWith('.rs'))) languages.add('rust');
        if (files.some((file) => file.endsWith('.php'))) languages.add('php');
        if (files.some((file) => file.endsWith('.rb'))) languages.add('ruby');
        if (files.some((file) => file.endsWith('.java') || file.endsWith('.kt'))) languages.add('jvm');
        if (files.some((file) => /(^|\/)src-tauri\/(Cargo\.toml|tauri\.conf\.(json|json5))$/.test(file))) {
            frameworks.add('tauri');
        }

        if (fileSet.has('pnpm-workspace.yaml')) workspaces.add('pnpm');
        if (fileSet.has('turbo.json')) workspaces.add('turborepo');
        if (fileSet.has('nx.json')) workspaces.add('nx');

        const manifests: ManifestEvidence[] = [];
        for (const projectRoot of projectRoots) {
            const manifestPath = projectRoot ? path.posix.join(projectRoot, 'package.json') : 'package.json';
            if (!fileSet.has(manifestPath)) continue;
            const manifest = await this.readPackageManifest(manifestPath);
            if (!manifest) continue;
            if (manifest.workspaces && !workspaces.has('pnpm')) workspaces.add('npm');
            for (const command of Object.keys(manifest.scripts ?? {})) commands.add(command);
            const dependencies = {
                ...manifest.peerDependencies,
                ...manifest.devDependencies,
                ...manifest.dependencies,
            };
            this.mapDependencies(dependencies, FRAMEWORK_DEPENDENCIES, frameworks);
            this.mapDependencies(dependencies, TEST_DEPENDENCIES, testing);
            this.mapDependencies(dependencies, DATABASE_DEPENDENCIES, database);
            manifests.push(this.manifestEvidence(manifestPath, manifest));
        }

        if (this.hasBasename(files, 'pyproject.toml') || this.hasBasename(files, 'requirements.txt')) languages.add('python');
        if (this.hasBasename(files, 'Cargo.toml')) languages.add('rust');
        if (this.hasBasename(files, 'go.mod')) languages.add('go');
        if (this.hasBasename(files, 'composer.json')) languages.add('php');
        if (this.hasBasename(files, 'pytest.ini') || this.hasBasename(files, 'conftest.py')) testing.add('pytest');
        if (files.some((file) => /(^|\/)playwright\.config\.[^/]+$/.test(file))) testing.add('playwright');
        if (files.some((file) => /(^|\/)prisma\/schema\.prisma$/.test(file))) database.add('prisma');
        if (files.some((file) => /(^|\/)drizzle\.config\.[^/]+$/.test(file))) database.add('drizzle');

        if (files.some((file) => ['wrangler.toml', 'wrangler.json', 'wrangler.jsonc'].includes(path.posix.basename(file)))) {
            deployment.add('cloudflare-workers');
        }
        if (files.some((file) => ['Dockerfile', 'docker-compose.yml', 'compose.yaml'].includes(path.posix.basename(file)))) {
            deployment.add('docker');
        }
        if (this.hasBasename(files, 'vercel.json')) deployment.add('vercel');
        if (this.hasBasename(files, 'netlify.toml')) deployment.add('netlify');
        if (files.some((file) => /(^|\/)\.github\/workflows\/.*\.ya?ml$/.test(file))) deployment.add('github-actions');

        const signals: ProjectInspectionSignals = {
            languages: sorted(languages),
            frameworks: sorted(frameworks),
            workspaces: sorted(workspaces),
            commands: sorted(commands),
            testing: sorted(testing),
            database: sorted(database),
            deployment: sorted(deployment),
            activeIdes: sorted(activeIdeIds),
        };
        return {
            signals,
            evidence: {
                manifests: manifests.sort((left, right) => compareCodeUnits(left.path, right.path)),
                configs: files.filter((file) => this.isConfigEvidence(file)).sort(compareCodeUnits),
            },
        };
    }

    private hasBasename(files: string[], basename: string): boolean {
        return files.some((file) => path.posix.basename(file) === basename);
    }

    private isConfigEvidence(file: string): boolean {
        const basename = path.posix.basename(file);
        return [
            'tsconfig.json',
            'pnpm-workspace.yaml',
            'turbo.json',
            'nx.json',
            'pyproject.toml',
            'requirements.txt',
            'Cargo.toml',
            'go.mod',
            'composer.json',
            'pytest.ini',
            'conftest.py',
            'wrangler.toml',
            'wrangler.json',
            'wrangler.jsonc',
            'Dockerfile',
            'docker-compose.yml',
            'compose.yaml',
            'vercel.json',
            'netlify.toml',
        ].includes(basename) || /(^|\/)(playwright|drizzle)\.config\.[^/]+$/.test(file)
            || /(^|\/)prisma\/schema\.prisma$/.test(file)
            || /(^|\/)\.github\/workflows\/.*\.ya?ml$/.test(file);
    }

    private manifestEvidence(manifestPath: string, manifest: PackageManifest): ManifestEvidence {
        const dependencies: ManifestDependencyEvidence[] = [];
        const sections: ManifestDependencyEvidence['section'][] = [
            'dependencies',
            'devDependencies',
            'peerDependencies',
        ];
        for (const section of sections) {
            for (const [name, version] of Object.entries(manifest[section] ?? {})) {
                dependencies.push({ section, name, version: String(version) });
            }
        }
        dependencies.sort((left, right) => {
            return compareCodeUnits(left.section, right.section)
                || compareCodeUnits(left.name, right.name)
                || compareCodeUnits(left.version, right.version);
        });
        const scripts = Object.entries(manifest.scripts ?? {})
            .map(([name, body]) => ({ name, body: String(body) }))
            .sort((left, right) => compareCodeUnits(left.name, right.name) || compareCodeUnits(left.body, right.body));
        return { path: manifestPath, dependencies, scripts };
    }

    private async readPackageManifest(relativePath: string): Promise<PackageManifest | null> {
        try {
            const content = await fs.readFile(path.join(this.projectRoot, relativePath), 'utf-8');
            return JSON.parse(content) as PackageManifest;
        } catch {
            return null;
        }
    }

    private mapDependencies(
        dependencies: Record<string, unknown>,
        mapping: Record<string, string>,
        target: Set<string>
    ): void {
        for (const dependency of Object.keys(dependencies)) {
            const signal = mapping[dependency];
            if (signal) target.add(signal);
        }
    }

    private changedSignals(
        current: ProjectInspectionSignals,
        baseline: ProjectInspectionSignals | undefined,
        changed: boolean
    ): Array<ProjectSignalGroup | 'fingerprint'> {
        if (!changed) return [];
        if (!baseline) return ['fingerprint'];
        const groups: Array<ProjectSignalGroup | 'fingerprint'> = SIGNAL_GROUPS.filter((group) => {
            return JSON.stringify(current[group]) !== JSON.stringify(baseline[group]);
        });
        if (groups.length === 0) groups.push('fingerprint');
        return groups;
    }
}
